package com.bitterinfant.webui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.fetch.RequestInit
import kotlin.js.json

/**
 * A password-protected link, shown as "🔒 [label]". Clicking it reveals a password field; the
 * password is POSTed to [endpoint] (a Cloudflare Pages Function, e.g. site-matthew's
 * `functions/api/best-friend.js`), which checks it server-side and replies `{ "url": ... }`.
 * On success the viewer is taken straight to that URL in a new tab (falling back to the current
 * tab if the browser blocks the popup, as Safari does for tabs opened after an async fetch), and
 * the row turns into a "🔓 [label]" link for returning to it. The password and destination URL
 * never appear in the page's JS bundle.
 */
@Composable
fun LockedLink(label: String, endpoint: String) {
    var open by remember { mutableStateOf(false) }
    var password by remember { mutableStateOf("") }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var url by remember { mutableStateOf<String?>(null) }

    fun submit() {
        if (busy || password.isEmpty()) return
        busy = true
        error = null
        val init = RequestInit(
            method = "POST",
            headers = json("content-type" to "application/json"),
            body = JSON.stringify(json("password" to password)),
        )
        window.fetch(endpoint, init)
            .then { res ->
                res.json().then { body ->
                    val result = body.asDynamic().url as? String
                    when {
                        res.ok && result != null -> {
                            url = result
                            password = ""
                            if (window.open(result, "_blank") == null) window.location.href = result
                        }
                        res.status.toInt() == 401 -> error = "Wrong password."
                        else -> error = "Couldn't unlock right now."
                    }
                }
            }
            .catch { error = "Couldn't unlock right now." }
            .then { busy = false }
    }

    Div(attrs = { classes("media-block") }) {
        val unlocked = url
        if (unlocked != null) {
            P(attrs = { style { marginTop(0.px); marginBottom(0.5.cssRem) } }) {
                A(href = unlocked, attrs = { attr("target", "_blank"); attr("rel", "noopener") }) {
                    B { Text("🔓 $label") }
                }
            }
            return@Div
        }

        P(attrs = { style { marginTop(0.px); marginBottom(0.5.cssRem) } }) {
            Button(attrs = {
                attr("aria-expanded", open.toString())
                onClick { open = !open }
                style {
                    property("background", "transparent")
                    property("border", "none")
                    padding(0.px)
                    property("cursor", "pointer")
                    property("font-family", Theme.fontStack)
                    fontSize(1.cssRem)
                    color(Color(Theme.textMuted))
                }
            }) { B { Text("🔒 $label") } }
        }

        if (open) {
            Form(attrs = {
                onSubmit { it.preventDefault(); submit() }
                style { display(DisplayStyle.Flex); property("gap", "0.5rem"); alignItems(AlignItems.Center); flexWrap(FlexWrap.Wrap) }
            }) {
                Input(InputType.Password, attrs = {
                    value(password)
                    onInput { password = it.value }
                    attr("placeholder", "Password")
                    attr("aria-label", "Password for $label")
                    attr("autocomplete", "off")
                    style {
                        property("background", Theme.bgElevated)
                        property("border", "1px solid ${Theme.border}")
                        property("border-radius", "4px")
                        color(Color(Theme.text))
                        padding(0.4.cssRem, 0.6.cssRem)
                        property("font-family", Theme.fontStack)
                    }
                })
                Button(attrs = {
                    attr("type", "submit")
                    if (busy) attr("disabled", "true")
                    style {
                        property("background", Theme.accent)
                        property("border", "none")
                        property("border-radius", "4px")
                        color(Color(Theme.text))
                        padding(0.4.cssRem, 0.9.cssRem)
                        property("cursor", "pointer")
                        property("font-family", Theme.fontStack)
                    }
                }) { Text(if (busy) "Checking…" else "Unlock") }
                error?.let { Span(attrs = { style { color(Color(Theme.accent)); fontSize(0.9.cssRem) } }) { Text(it) } }
            }
        }
    }
}
